import React, { useState } from 'react';
import { Layout, Menu, Dropdown, Avatar, Typography, theme, Button } from 'antd';
import {
  AppstoreOutlined,
  FileTextOutlined,
  FileSearchOutlined,
  FundOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DownOutlined,
  GlobalOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useKeycloak } from '../../contexts/KeycloakContext';
import { useTheme } from '../../contexts/ThemeContext';
import { UserRole } from '../../types/enums';
import { useQueryClient } from '@tanstack/react-query';
import { useRefreshAppsMutation } from '@/hooks/useAppsQueries';

const { Header, Sider, Content } = Layout;

export const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const { logout } = useKeycloak();
  const { isDark } = useTheme();
  const queryClient = useQueryClient();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();
  const refreshAppsMutation = useRefreshAppsMutation();

  const handleRefresh = async () => {
    await refreshAppsMutation.mutateAsync();
    await queryClient.invalidateQueries({ queryKey: ['templates'] });
  };
  const menuItems = [
    {
      key: '/apps',
      icon: <AppstoreOutlined />,
      label: t('navigation.apps'),
    },
    {
      key: '/templates',
      icon: <FileTextOutlined />,
      label: t('navigation.templates'),
    },
    {
      key: '/presets',
      icon: <AppstoreOutlined />,
      label: 'Пресети',
    },
    {
      key: '/landings',
      icon: <GlobalOutlined />,
      label: 'Лендинги',
    },
    {
      key: '/logs',
      icon: <FileSearchOutlined />,
      label: t('navigation.logs'),
    },
    {
      key: '/grafana-logs',
      icon: <FundOutlined />,
      label: 'Логи Grafana',
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleLogout = () => {
    // Keycloak logout вже виконує редирект
    logout();
  };

  const userMenuItems = [
    {
      key: 'profile',
      label: t('auth.profile'),
      icon: <UserOutlined />,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      label: t('auth.logout'),
      icon: <LogoutOutlined />,
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'profile') {
      navigate('/profile');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        trigger={null} 
        collapsible 
        collapsed={collapsed}
        style={{
          background: colorBgContainer,
          borderRight: `1px solid ${isDark ? '#424242' : '#f0f0f0'}`,
        }}
      >
        <div style={{ 
          height: 64, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          borderBottom: `1px solid ${isDark ? '#424242' : '#f0f0f0'}`
        }}>
          <Typography.Title level={collapsed ? 5 : 4} style={{ margin: 0, color: '#1890ff' }}>
            {collapsed ? 'TG' : 'TG Gray Manager'}
          </Typography.Title>
        </div>
        <Menu
          theme="light"
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header style={{ 
          padding: '0 16px', 
          background: colorBgContainer,
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => handleRefresh()}
              loading={refreshAppsMutation.isPending}
            >
              {t("apps.refreshApps")}
            </Button>
                      <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleUserMenuClick,
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <div 
              style={{ 
                cursor: 'pointer', 
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 8px',
                borderRadius: '6px',
                transition: 'background-color 0.2s ease'
              }}
              className="user-dropdown"
            >
              <Avatar 
                size={32} 
                style={{
                  backgroundColor: isDark ? '#262626' : '#f5f5f5',
                  color: isDark ? '#a6a6a6' : '#8c8c8c'
                }}
                icon={<UserOutlined />}
              />
              
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '0px',
                minWidth: 0,
                maxWidth: '120px'
              }}>
                <span style={{ 
                  fontSize: '14px',
                  color: isDark ? '#ffffff' : '#262626',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '1.2'
                }}>
                  {user?.name || user?.email}
                </span>
                <span style={{ 
                  fontSize: '12px',
                  color: isDark ? '#a6a6a6' : '#8c8c8c',
                  fontWeight: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: '1.2'
                }}>
                  {user?.role === UserRole.ADMIN ? t('roles.admin') : t('roles.user')}
                </span>
              </div>
              
              <DownOutlined style={{ 
                color: isDark ? '#a6a6a6' : '#8c8c8c', 
                fontSize: '12px'
              }} />
            </div>
          </Dropdown>
          </div>     
        </Header>
        <Content
          style={{
            margin: '24px 16px',
            padding: 24,
            minHeight: 280,
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};